import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { GradeTierBadge, type GradeTier } from "@/components/grade-tier-badge";
import { resolveProductImage } from "@/lib/productTypeImages";
import { optimizedSrcSet } from "@/lib/imageOptimize";
import { formatMoney, parsePrice } from "@shared/currency";
import { useAuth } from "@/hooks/useAuth";
import { TruckSelectionModal } from "@/components/truck-selection-modal";
import { SplashWelcomeModal } from "@/components/splash-welcome-modal";
import { ArrowRight, Boxes, Sparkles, Refrigerator } from "lucide-react";

// Per-category tile icon. Admin-uploaded `iconUrl` wins; otherwise we fall
// back to a sensible default — refrigerator for refrigerators, stacked boxes
// (i.e. a pallet) for everything else.
function CategoryIcon({
  slug,
  iconUrl,
  className,
}: {
  slug: string;
  iconUrl?: string | null;
  className?: string;
}) {
  if (iconUrl) {
    return (
      <img
        src={iconUrl}
        alt=""
        className={`object-contain ${className ?? ""}`}
        aria-hidden
        data-testid={`tile-icon-${slug}`}
      />
    );
  }
  const Icon = slug === "refrigerators" ? Refrigerator : Boxes;
  return <Icon className={className} aria-hidden />;
}

const WELCOME_SEEN_KEY = "openbox:welcomeSeen";

interface CategoryTile {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  showroomImage: string | null;
  iconUrl: string | null;
  heroBlurb: string | null;
  inStockCount: number;
  startingPrice: number | null;
  // Task #126 — per-category sale type drives count noun + per-item suffix.
  saleType?: "products" | "pallets";
  countNounSingular?: string;
  countNounPlural?: string;
  totalUnitCount?: number;
  modelCountNounSingular?: string;
  modelCountNounPlural?: string;
  unitCountNounSingular?: string;
  unitCountNounPlural?: string;
  perItemStartingPrice?: number | null;
}

interface FeaturedProduct {
  modelId: string;
  brand: string;
  modelName: string;
  description: string;
  imageUrl: string | null;
  startingPrice: number;
  category: { id: string; slug: string; name: string } | null;
  grades: string[];
}

interface LandingResponse {
  categories: CategoryTile[];
  featured: FeaturedProduct[];
}

// Mirrors server/services/categoryNavigation.ts:tierForGrade so colors match.
function tierForGrade(code: string): GradeTier {
  const cx = (code || "").toUpperCase().trim();
  if (cx === "X" || cx === "FACTORY REFURBISHED") return "refurb";
  return _tierForGradeBase(code);
}
function _tierForGradeBase(code: string): GradeTier {
  const c = (code || "").toUpperCase().trim();
  if (c === "A" || c === "TIER 1" || c === "T1" || c === "B" || c === "B (FACTORY REFURBISHED)") return "high";
  if (c === "B+" || c === "TIER 2" || c === "T2") return "mid";
  if (c === "B-" || c === "TIER 3" || c === "T3" || c === "C") return "low";
  return "mid";
}

function CategoryTileCard({ tile }: { tile: CategoryTile }) {
  return (
    <Link href={`/category/${tile.slug}`}>
      <Card
        className="group overflow-hidden h-full cursor-pointer hover:shadow-xl transition-all border-border/60 hover:border-primary/40"
        data-testid={`tile-category-${tile.slug}`}
      >
        <div className="relative aspect-[4/3] bg-gradient-to-br from-muted to-muted/40 overflow-hidden">
          {tile.showroomImage ? (
            <picture>
              <source
                type="image/webp"
                srcSet={optimizedSrcSet(tile.showroomImage, [400, 800, 1200]) || undefined}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              />
              <img
                src={tile.showroomImage}
                alt={tile.name}
                width={800}
                height={600}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </picture>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground/50">
              <Boxes className="h-20 w-20" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
            <h3 className="text-xl md:text-2xl font-bold text-white drop-shadow-lg" data-testid={`tile-name-${tile.slug}`}>
              {tile.name}
            </h3>
            {(() => {
              // Pallet categories show a single per-item "from $X per item"
              // pill (Task #126) — that's the price a customer actually pays
              // per individual unit they receive inside any in-stock pallet.
              // If no per-item price is computable, the pill is omitted (we
              // intentionally do NOT fall back to startingPrice, which is the
              // pallet total). Product categories keep the original "from $Y".
              if (tile.saleType === "pallets") {
                // Only render the per-item pill when we have a real
                // constituent unit price. Falling back to startingPrice
                // (which is the pallet price) would mislabel a pallet
                // total as a per-item price.
                if (tile.perItemStartingPrice == null) return null;
                return (
                  <span
                    className="bg-white/95 text-foreground rounded-full px-2.5 py-1 text-xs font-semibold shadow"
                    data-testid={`tile-price-${tile.slug}`}
                  >
                    from {formatMoney(parsePrice(tile.perItemStartingPrice))} per item
                  </span>
                );
              }
              if (tile.startingPrice == null) return null;
              return (
                <span
                  className="bg-white/95 text-foreground rounded-full px-2.5 py-1 text-xs font-semibold shadow"
                  data-testid={`tile-price-${tile.slug}`}
                >
                  from {formatMoney(parsePrice(tile.startingPrice))}
                </span>
              );
            })()}
          </div>
        </div>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
              <CategoryIcon
                slug={tile.slug}
                iconUrl={tile.iconUrl}
                className="h-4 w-4 text-muted-foreground shrink-0"
              />
              <span className="font-semibold text-foreground" data-testid={`tile-count-${tile.slug}`}>
                {tile.inStockCount}
              </span>{" "}
              {tile.inStockCount === 1
                ? (tile.modelCountNounSingular ?? tile.countNounSingular ?? "model")
                : (tile.modelCountNounPlural ?? tile.countNounPlural ?? "models")}
              {typeof tile.totalUnitCount === "number" && tile.totalUnitCount > 0 && (
                <>
                  {" — "}
                  <span className="font-semibold text-foreground" data-testid={`tile-unit-count-${tile.slug}`}>
                    {tile.totalUnitCount}
                  </span>{" "}
                  total{" "}
                  {tile.totalUnitCount === 1
                    ? (tile.unitCountNounSingular ?? "unit")
                    : (tile.unitCountNounPlural ?? "units")}
                </>
              )}
            </span>
            <ArrowRight className="h-4 w-4 text-primary group-hover:translate-x-0.5 transition-transform" />
          </div>
          {tile.heroBlurb && (
            <p
              className="text-sm font-semibold text-foreground leading-snug line-clamp-3"
              data-testid={`tile-blurb-${tile.slug}`}
            >
              {tile.heroBlurb}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function FeaturedDealCard({ p }: { p: FeaturedProduct }) {
  const img = resolveProductImage(p.imageUrl ?? undefined, p.description, p.brand);
  return (
    <Link href={`/product/${p.modelId}`}>
      <Card
        className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow h-full"
        data-testid={`featured-${p.modelId}`}
      >
        <div className="relative h-44 sm:h-56 bg-muted">
          <picture>
            <source
              type="image/webp"
              srcSet={optimizedSrcSet(img, [200, 400, 800]) || undefined}
              sizes="(max-width: 640px) 50vw, 33vw"
            />
            <img
              src={img}
              alt={`${p.brand} ${p.modelName}`}
              width={400}
              height={300}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-contain"
            />
          </picture>
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full shadow">
            <Sparkles className="h-3 w-3" /> Featured
          </span>
        </div>
        <CardContent className="p-3">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide truncate">{p.brand}</p>
          <h3 className="font-semibold text-sm text-foreground line-clamp-1">{p.modelName}</h3>
          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{p.description}</p>
          <div className="flex items-center justify-between mt-2">
            <span className="text-base font-bold text-primary">
              {formatMoney(parsePrice(p.startingPrice))}
            </span>
            <div className="flex gap-1">
              {p.grades.slice(0, 3).map((g) => (
                <GradeTierBadge key={g} code={g} tier={tierForGrade(g)} compact />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function ShowroomLanding() {
  const { isLoading: authLoading, user } = useAuth();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  useEffect(() => {
    document.title = "Shop by Category | OpenBox by ShopJimmy";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    if (authLoading) return;
    const urlParams = new URLSearchParams(window.location.search);
    const forceShow = urlParams.get("showWelcome") === "true";
    if (forceShow) {
      setShowWelcomeModal(true);
      return;
    }
    if (!user) {
      const seen = localStorage.getItem(WELCOME_SEEN_KEY);
      if (!seen) setShowWelcomeModal(true);
    }
  }, [authLoading, user]);

  const handleCloseWelcome = () => {
    localStorage.setItem(WELCOME_SEEN_KEY, "true");
    setShowWelcomeModal(false);
  };

  const { data, isLoading } = useQuery<LandingResponse>({
    queryKey: ["/api/categories/landing"],
  });

  const cats = data?.categories ?? [];

  return (
    <div className="min-h-screen bg-background">
      <SplashWelcomeModal open={showWelcomeModal} onClose={handleCloseWelcome} />
      <TruckSelectionModal />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground" data-testid="text-landing-title">
            Shop by Category
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl" data-testid="text-landing-subtitle">
            Browse open-box and factory-refurbished appliances, electronics, and fitness gear — every unit fully functional with the original factory warranty.
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="aspect-[4/3] rounded-xl" />
            ))}
          </div>
        ) : cats.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="empty-categories">
            <Boxes className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No categories available yet. Check back soon.</p>
          </div>
        ) : (
          (() => {
            // Custom row layout so the user can reserve the top row for the
            // most important categories (e.g. Refrigerators + Shark/Ninja).
            //   ≤3 → single row of N
            //    4 → 2,2     5 → 2,3     6 → 3,3
            // Beyond 6 we revert to a 3-up flowing grid (still readable, but
            // the curated split logic doesn't apply).
            const n = cats.length;
            let topCount: number;
            if (n <= 3) topCount = n;
            else if (n === 4) topCount = 2;
            else if (n === 5) topCount = 2;
            else if (n === 6) topCount = 3;
            else topCount = n; // >6 → single flowing grid below

            const useTwoRows = n >= 4 && n <= 6;
            const top = useTwoRows ? cats.slice(0, topCount) : cats;
            const bottom = useTwoRows ? cats.slice(topCount) : [];

            const rowGridClass = (count: number) => {
              if (count <= 1) return "grid grid-cols-1 gap-4 sm:gap-6";
              if (count === 2) return "grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6";
              return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6";
            };

            return (
              <div className="space-y-4 sm:space-y-6" data-testid="grid-categories">
                <div className={rowGridClass(top.length)} data-testid="grid-categories-row-top">
                  {top.map((c) => (
                    <CategoryTileCard key={c.id} tile={c} />
                  ))}
                </div>
                {bottom.length > 0 && (
                  <div
                    className={rowGridClass(bottom.length)}
                    data-testid="grid-categories-row-bottom"
                  >
                    {bottom.map((c) => (
                      <CategoryTileCard key={c.id} tile={c} />
                    ))}
                  </div>
                )}
              </div>
            );
          })()
        )}
      </section>

      {data?.featured && data.featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="section-featured">
          <div className="flex items-end justify-between mb-4">
            <h2 className="text-2xl font-bold text-foreground">Featured Deals</h2>
            <span className="text-xs text-muted-foreground">Hand-picked savings</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.featured.map((p) => (
              <FeaturedDealCard key={p.modelId} p={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
